/* eslint-disable prettier/prettier */
'use client';

import * as React from 'react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Search, Tag, Check, Plus } from 'lucide-react';
import { useProductsSlice } from '../slice';
import useDebounce from 'utils/hooks/debounce-hook';

export default function Tags() {
  const form = useFormContext();
  const [search, setSearch] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState<any[]>([]);
  const [showTagsList, setShowTagsList] = React.useState(true);
  const searchDebounce = useDebounce(search, 300);

  // Initialize selected tags from form data
  React.useEffect(() => {
    const formTags = form.getValues('tags') || [];
    const formNewTags = form.getValues('newTags') || [];
    const allTags = [
      ...formTags,
      ...formNewTags.map((tagName, index) => ({
        tag_id: `new_${index}`,
        tag_name: tagName,
      })),
    ];
    if (allTags.length > 0) {
      setSelectedTags(allTags);
    }
  }, [form]);

  const { useLazyGetProductsTagsQuery } = useProductsSlice();
  const [getTags, { data: listData, isLoading }] =
    useLazyGetProductsTagsQuery();

  // Memoize tags to prevent unnecessary re-renders
  const tags = useMemo(
    () => listData?.response?.data || [],
    [listData?.response?.data],
  );

  // Load tags on mount and when search changes
  React.useEffect(() => {
    getTags({
      search: searchDebounce,
    });
  }, [searchDebounce, getTags]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleTagSelect = React.useCallback(
    (tag: any) => {
      const isSelected = selectedTags.some(t => t.tag_id === tag.tag_id);

      if (isSelected) {
        // Remove tag
        const updated = selectedTags.filter(t => t.tag_id !== tag.tag_id);
        setSelectedTags(updated);

        // Update form data
        const existingTags = updated.filter(
          t => !t.tag_id.toString().startsWith('new_'),
        );
        const newTags = updated
          .filter(t => t.tag_id.toString().startsWith('new_'))
          .map(t => t.tag_name);

        form.setValue('tags', existingTags);
        form.setValue('newTags', newTags);
      } else {
        // Add tag
        const updated = [...selectedTags, tag];
        setSelectedTags(updated);

        // Update form data
        const existingTags = updated.filter(
          t => !t.tag_id.toString().startsWith('new_'),
        );
        const newTags = updated
          .filter(t => t.tag_id.toString().startsWith('new_'))
          .map(t => t.tag_name);

        form.setValue('tags', existingTags);
        form.setValue('newTags', newTags);

        // Clear search and close tags list after selection
        setSearch('');
        setShowTagsList(false);
      }
    },
    [selectedTags, form],
  );

  const handleAddNewTag = React.useCallback(() => {
    if (search.trim() === '') return;

    const newTag = { tag_id: `new_${Date.now()}`, tag_name: search.trim() };
    const updated = [...selectedTags, newTag];
    setSelectedTags(updated);

    // Update form data
    const existingTags = updated.filter(
      t => !t.tag_id.toString().startsWith('new_'),
    );
    const newTags = updated
      .filter(t => t.tag_id.toString().startsWith('new_'))
      .map(t => t.tag_name);

    form.setValue('tags', existingTags);
    form.setValue('newTags', newTags);

    setSearch('');
    setShowTagsList(false);
  }, [search, selectedTags, form]);

  const isTagSelected = React.useCallback(
    (tagId: string | number) => {
      return selectedTags.some(t => t.tag_id === tagId);
    },
    [selectedTags],
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      // Show tags list when user starts typing
      if (e.target.value) {
        setShowTagsList(true);
      }
    },
    [],
  );

  // Memoize selected tags section
  const selectedTagsSection = React.useMemo(() => {
    if (selectedTags.length === 0) return null;

    return (
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Selected Tags ({selectedTags.length})
        </h3>
        <div className="flex flex-wrap gap-2">
          {selectedTags.map(tag => (
            <Badge
              key={tag.tag_id}
              variant="secondary"
              className={`flex items-center gap-1 px-3 py-1 ${tag.tag_id.toString().startsWith('new_')
                  ? 'bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 border border-emerald-200 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200'
                  : 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 border border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-200'
                }`}
            >
              <Tag className="w-3 h-3" />
              {tag.tag_name}
              <button
                onClick={() => handleTagSelect(tag)}
                className="ml-1 hover:text-red-500"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>
    );
  }, [selectedTags, handleTagSelect]);

  // Memoize tags list section
  const tagsListSection = React.useMemo(() => {
    if (!showTagsList) return null;

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <div className="p-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {search ? 'Search Results' : 'All Tags'}
          </h3>
        </div>
        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              Loading tags...
            </div>
          ) : tags.length > 0 ? (
            <>
              {tags.map(tag => (
                <div
                  key={tag.tag_id}
                  className={`p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${isTagSelected(tag.tag_id)
                      ? 'bg-purple-50 dark:bg-purple-900/20'
                      : ''
                    }`}
                  onClick={() => handleTagSelect(tag)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-6 h-6 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded flex items-center justify-center flex-shrink-0">
                        <Tag className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                          {tag.tag_name}
                        </div>
                      </div>
                    </div>
                    {isTagSelected(tag.tag_id) && (
                      <Check className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    )}
                  </div>
                </div>
              ))}
              {/* Add new tag option */}
              {search.trim() !== '' &&
                !tags.some(
                  tag => tag.tag_name.toLowerCase() === search.toLowerCase(),
                ) && (
                  <div
                    className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    onClick={handleAddNewTag}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded flex items-center justify-center flex-shrink-0">
                        <Plus className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                          Add "{search}"
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </>
          ) : (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {search ? (
                <div>
                  <p>No tags found</p>
                  {search.trim() !== '' && (
                    <Button
                      className="mt-2 h-8 text-xs"
                      onClick={handleAddNewTag}
                      variant="outline"
                    >
                      <Plus className="mr-1 w-3 h-3" />
                      Add "{search}"
                    </Button>
                  )}
                </div>
              ) : (
                'No tags available'
              )}
            </div>
          )}
        </div>
      </div>
    );
  }, [
    showTagsList,
    search,
    isLoading,
    tags,
    isTagSelected,
    handleTagSelect,
    handleAddNewTag,
  ]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex shrink-0 items-center justify-center">
            <Tag className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Product Tags
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Search and select tags for your product
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Type to search tags..."
            value={search}
            onChange={handleSearchChange}
            className="pl-10 h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 rounded-lg"
          />
        </div>

        {/* Selected Tags */}
        {selectedTagsSection}

        {/* Tags List */}
        {tagsListSection}
      </div>
    </div>
  );
}
